<template>
  <v-dialog ref="shareDialog" v-model="dialogShare" max-width="50%" persistent>
    <template v-slot:activator="{ props }">
      <v-list-item v-bind="props" :disabled="!isOwner">
        <template v-slot:prepend>
          <v-icon color="primary">mdi-account-plus</v-icon>
        </template>
        <v-list-item-title>
          {{ $t("labels.share") }}
        </v-list-item-title>
      </v-list-item>
    </template>
    <v-card height="600px" class="overflow-auto">
      <v-toolbar flat color="secondary">
        <v-toolbar-title class="text-white">
          {{ $t("labels.share-model") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon color="white" @click="close()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <h3 class="text-primary font-weight-medium">
                {{ $t("labels.share-to-collaborate") }}
              </h3>

              <small class="text-grey">
                {{ $t("labels.add-users-to-collaborate") }}
              </small>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="7">
              <v-autocomplete
                v-model="selectedUser"
                :items="availableUsers"
                :no-data-text="$t('labels.no-data-available')"
                :placeholder="$t('labels.search-user')"
                item-title="username"
                item-value="id"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="selectedRole"
                :placeholder="$t('labels.role')"
                :items="roles"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col cols="2">
              <v-btn
                color="primary"
                block
                :disabled="!selectedUser || !selectedRole"
                @click="addShare"
              >
                {{ $t("labels.add") }}
              </v-btn>
            </v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-row v-if="owner">
            <v-col cols="12">
              <div class="text-subtitle-2 mb-2">
                {{ $t("labels.owner-section") }}
              </div>
              <v-list density="comfortable">
                <v-list-item>
                  <template #prepend>
                    <v-icon class="text-h4 text-primary">
                      mdi-account-circle
                    </v-icon>
                  </template>
                  <v-list-item-title>
                    {{ owner.username }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ owner.email }}
                  </v-list-item-subtitle>
                  <template #append>
                    <v-chip color="primary" size="small">
                      {{ $t("labels.owner") }}
                    </v-chip>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-2">
                {{ $t("labels.shared-users") }}
              </div>
              <v-list two-line>
                <template v-if="sharedWithoutOwner.length">
                  <v-list-item
                    v-for="(shared, i) in sharedWithoutOwner"
                    :key="i"
                  >
                    <template #prepend>
                      <v-icon class="text-h4 text-primary">
                        mdi-account-circle
                      </v-icon>
                    </template>
                    <v-list-item-title>
                      {{ shared.username }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ shared.email }}
                    </v-list-item-subtitle>
                    <template #append>
                      <v-select
                        :model-value="getRole(shared)"
                        :items="roles"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        density="compact"
                        hide-details
                        style="width: 120px"
                        @update:modelValue="updateRole(shared, $event)"
                      />
                      <v-icon class="ml-2" @click="unshareUser(shared.id)">
                        mdi-close
                      </v-icon>
                    </template>
                  </v-list-item>
                </template>
                <div v-else>
                  {{ $t("labels.no-shared") }}
                </div>
              </v-list>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider class="mt-2"></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close()">{{ $t("labels.cancel") }}</v-btn>

        <v-btn
          :loading="loading"
          @click="submitShare()"
          :text="$t('labels.share')"
          color="secondary"
          variant="text"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { inject } from "vue";
import events from "@/mixins/events.js";

export default {
  name: "DialogShare",
  props: ["tenantId", "apiUrl", "item", "isOwner"],
  emits: ["openSnackbar"],
  mixins: [events],
  data() {
    return {
      dialogShare: false,
      loading: false,
      users: [],
      collaborators: [],
      selectedUser: null,
      selectedRole: null,
      axios: inject("axios"),
      opts: inject("opts"),
    };
  },
  computed: {
    sharedWithoutOwner() {
      console.log(this.collaborators);
      return this.collaborators.filter((u) => !u.permissions?.ownership);
    },
    availableUsers() {
      if (!this.users) return [];

      const sharedIds = this.collaborators.map((u) => u.id);
      return this.users.filter((u) => !sharedIds.includes(u.id));
    },
    owner() {
      return this.collaborators.find((e) => e?.permissions?.ownership);
    },
    roles() {
      return [
        { label: this.$t("labels.reader-select"), value: "reader" },
        { label: this.$t("labels.editor-select"), value: "editor" },
      ];
    },
  },
  watch: {
    dialogShare(val) {
      if (val) this.initUsers();
    },
  },
  methods: {
    initUsers() {
      this.axios
        .all([
          this.axios.get(
            `${this.apiUrl}/${this.tenantId}/models/share/${this.item.modelId}`,
            this.opts,
          ),
          this.axios.get(
            `${this.apiUrl}/${this.tenantId}/models/share/users`,
            this.opts,
          ),
        ])
        .then(
          this.axios.spread((allCollaborators, allUsers) => {
            this.users = allUsers.data;
            this.collaborators = allCollaborators.data;
          }),
        )
        .catch((err) => {
          this.setError(err, false);
        });
    },
    addShare() {
      this.collaborators.push({
        ...this.availableUsers.find((e) => e.id == this.selectedUser),
        permissions: {
          ownership: false,
          capabilities: {
            canRead: true,
            canWrite: this.selectedRole === "editor",
          },
        },
      });

      this.selectedUser = null;
      this.selectedRole = null;
    },
    unshareUser(userId) {
      this.collaborators = this.collaborators.filter((u) => u.id !== userId);
    },
    submitShare() {
      this.loading = true;

      this.axios
        .put(
          `${this.apiUrl}/${this.tenantId}/models/share/${this.item.modelId}`,
          this.collaborators,
          {
            withCredentials: true,
          },
        )
        .then((response) => {
          if (response.status === 200) {
            this.$emit("openSnackbar", true, this.$t("strings.dtm-shared"));
            this.close();
          }
        })
        .catch((err) => {
          this.setError(err, false);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getRole(user) {
      if (user?.permissions?.capabilities?.canWrite) return "editor";
      return "reader";
    },
    updateRole(shared, newRole) {
      shared.permissions.capabilities.canRead = true;
      shared.permissions.capabilities.canWrite = newRole === "editor";
    },

    close() {
      this.users = null;
      this.collaborators = [];
      this.selectedUser = null;
      this.selectedRole = null;
      this.dialogShare = false;
    },
  },
};
</script>
